import { FunctionComponent, useCallback, useState } from "react";
import { Form } from "@ui-kit/Form/Form";
import { useAppDispatch, useAppSelector } from "@store/store";
import { useTranslation } from "react-i18next";
import {
  selectEmail,
  selectAuthView,
  setAuthViewAction,
} from "@store/sessionReducer";
import { EmailField } from "../../../../components/formFields/EmailField/EmailField";
import { FormStatus } from "../../../../components/formFields/FormStatus";
import { AuthSubmitButton } from "../../components/AuthSubmitButton/AuthSubmitButton";
import { AuthLinkButton } from "../../components/AuthLinkButton/AuthLinkButton";
import { AuthInstructions } from "../../components/AuthInstructions/AuthInstructions";
import { AuthError } from "../../components/AuthError/AuthError";
import { resetPasswordRequestAction } from "@store/auth/actions/resetPasswordRequestAction";

interface ResetPasswordRequestProps {}

const ResetPasswordRequest: FunctionComponent<
  ResetPasswordRequestProps
> = () => {
  const [state, setState] = useState<{ status: FormStatus }>({
    status: "idle",
  });
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const email = useAppSelector(selectEmail);
  const view = useAppSelector(selectAuthView);

  const isNewRequest = view === "resetPasswordRequest";
  const isExpiredRequest = view === "resetPasswordExpired";
  const isExpiredWithStoredEmail = isExpiredRequest && !!email;
  const instructionValues = isExpiredRequest
    ? {
        additionalDescription: isExpiredWithStoredEmail
          ? t("screens.auth.resetPasswordExpired.withStoredEmail")
          : t("screens.auth.resetPasswordExpired.withoutStoredEmail"),
      }
    : undefined;

  const handleSubmit = useCallback(
    async (values: { email: string }) => {
      setState({ status: "fetching" });
      try {
        await dispatch(
          resetPasswordRequestAction({ email: values.email, captcha: "" }),
        );
        setState({ status: "success" });
      } catch (_error) {
        setState({ status: "error" });
      }
    },
    [dispatch],
  );

  const handleGoBack = useCallback(() => {
    dispatch(setAuthViewAction({ authView: "signInPassword" }));
  }, [dispatch]);

  const disabled = state.status === "fetching" || state.status === "success";

  return (
    <>
      <AuthInstructions values={instructionValues} />
      <div className="form-container">
        <Form
          form={form}
          name="resetPasswordRequest"
          layout="vertical"
          onFinish={handleSubmit}
          disabled={disabled}
          initialValues={{ email: email ?? "" }}>
          <EmailField
            autoComplete="email"
            disabled={isNewRequest}
            hidden={isExpiredWithStoredEmail}
          />

          <div className="form-button-container">
            <AuthSubmitButton
              label={t(`screens.auth.${view}.submit`)}
              disabled={disabled}
            />
            {isNewRequest && (
              <AuthLinkButton
                label={t("common.goBack")}
                onPress={handleGoBack}
                disabled={disabled}
              />
            )}
          </div>
        </Form>
      </div>

      {state.status === "error" && <AuthError />}
    </>
  );
};

export { ResetPasswordRequest };
